import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Product mode prices one product in a throwaway cart created through the
 * Storefront API, so the customer's own cart is never touched. Carrier-calculated
 * rates (Frenet) only come back with `withCarrierRates: true`, which the API only
 * accepts under `@defer` - hence the multipart response read in #readGraphQL.
 */
const STOREFRONT_ENDPOINT = '/api/2026-07/graphql.json';

const PRODUCT_RATES_QUERY = `mutation YunaProductShippingRates($lines: [CartLineInput!], $delivery: CartDeliveryInput) {
  cartCreate(input: { lines: $lines, delivery: $delivery }) {
    cart {
      id
      ... @defer {
        deliveryGroups(first: 5, withCarrierRates: true) {
          nodes {
            deliveryOptions { handle title description estimatedCost { amount currencyCode } }
          }
        }
      }
    }
    userErrors { field message }
  }
}`;

/**
 * Estimates shipping from a postal code, using Shopify's own cart rates.
 *
 * Rates come from `/cart/shipping_rates.json`, so whatever the store has set up —
 * flat zones, carrier-calculated, an app — is exactly what the customer sees,
 * with no second source of truth to keep in sync.
 *
 * Why the address lookup: this store's rates endpoint rejects a request that has
 * no province, with 422 `{"province":["Selecione um estado"]}`. A Brazilian
 * customer only ever types a CEP, so the state has to be resolved first. ViaCEP
 * is used for that, and its answer also lets the estimate name the city back to
 * the customer, which is how they can tell the CEP matched what they meant.
 *
 * Verified against the live store: RS, RJ, MG, BA and AM all return Jadlog, PAC
 * and Sedex with delivery estimates, and parts of São Paulo also return a local
 * delivery rate carrying a description.
 *
 * Carrier rates are fetched asynchronously by Shopify. The first request for a
 * postal code it hasn't priced yet answers `200` with `shipping_rates: []` while
 * it calls the carrier in the background — an empty list is "not ready", not
 * "nothing ships here", and the two are indistinguishable in a single response.
 * Measured on the live store, the first call came back empty in ~640 ms and the
 * next one carried all three rates. Asking once made the calculator tell most
 * customers no carrier served them, so #fetchRates asks again (see RETRY_DELAYS)
 * before believing an empty answer. A zone with genuinely no rate still ends up
 * empty; it just takes the full set of attempts to say so.
 *
 * On the product page (data-mode="product") the same interface prices the
 * selected variant on its own instead, because the cart endpoint can only price
 * what is already in the cart. See #fetchProductRates.
 *
 * @extends {Component<{
 *   form: HTMLFormElement,
 *   input: HTMLInputElement,
 *   button: HTMLButtonElement,
 *   results: HTMLElement,
 *   status: HTMLElement,
 * }>}
 */
class YunaShippingCalculator extends Component {
  requiredRefs = ['form', 'input', 'results', 'status'];

  /** Remembers the last postal code so a returning customer doesn't retype it. */
  static STORAGE_KEY = 'yuna:shipping-cep';

  /** @type {AbortController | null} */
  #activeRequest = null;

  /** The CEP of the estimate on screen, so a variant change can redo it. */
  #lastPostalCode = '';

  /** @type {Element | null} Where the product select listener is attached. */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    // The variant picker dispatches Shopify's standard product select event;
    // like the theme's own price block, listen for it on the section.
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    const { input } = this.refs;
    if (input && !input.value) {
      try {
        const saved = localStorage.getItem(YunaShippingCalculator.STORAGE_KEY);
        if (saved) input.value = this.#format(saved);
      } catch {
        // Private browsing, or storage disabled — the field just starts empty.
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section = null;
    this.#activeRequest?.abort();
  }

  /**
   * Product page: pack sizes weigh differently, so an estimate already on screen
   * goes stale when the customer picks another variant. It is redone for the same
   * CEP - only when one is showing, never unprompted.
   * @param {Event} event - Shopify's standard product select event
   */
  #handleProductSelect = (event) => {
    if (this.dataset.mode !== 'product') return;
    // Product cards in the same section fire it too.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { promise } = /** @type {{ promise?: Promise<any> }} */ (/** @type {unknown} */ (event));
    promise
      ?.then(({ variant, detail } = {}) => {
        if (detail?.productId && String(detail.productId) !== String(this.dataset.productId)) return;

        // The rendered variant JSON carries a numeric id; the standard payload a GID.
        const id = String(detail?.resource?.id ?? variant?.id ?? '').split('/').pop();
        if (!id || id === this.dataset.variantId) return;
        this.dataset.variantId = id;

        if (this.#lastPostalCode) this.#estimate(this.#lastPostalCode);
      })
      .catch(() => {
        // An aborted variant fetch: the next selection brings its own event.
      });
  };

  get #country() {
    return this.dataset.country || 'Brazil';
  }

  get #isBrazil() {
    return /^(brazil|brasil|br)$/i.test(this.#country);
  }

  /** Digits only — the customer may type dots and a dash. */
  #digits(value) {
    return String(value || '').replace(/\D+/g, '');
  }

  /** Presentation form for a Brazilian CEP: 00000-000. */
  #format(value) {
    const digits = this.#digits(value).slice(0, 8);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  /**
   * Keeps the field readable while typing without fighting the caret: the mask is
   * only written back when it actually changes the value.
   * @param {Event} event
   */
  maskInput = (event) => {
    if (!this.#isBrazil) return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const masked = this.#format(target.value);
    if (masked !== target.value) target.value = masked;
  };

  /**
   * @param {SubmitEvent} event
   */
  calculate = async (event) => {
    event.preventDefault();

    const { input } = this.refs;
    const postalCode = this.#digits(input.value);

    if (!postalCode || (this.#isBrazil && postalCode.length !== 8)) {
      this.#renderMessage(this.dataset.errorInvalid, 'error');
      input.focus();
      return;
    }

    try {
      localStorage.setItem(YunaShippingCalculator.STORAGE_KEY, postalCode);
    } catch {
      // Not remembering it isn't worth interrupting the estimate.
    }

    await this.#estimate(postalCode);
  };

  /**
   * Resolves the CEP and renders its rates. Shared by the form and, on the
   * product page, by variant changes.
   * @param {string} postalCode - digits only
   */
  async #estimate(postalCode) {
    this.#activeRequest?.abort();
    const controller = new AbortController();
    this.#activeRequest = controller;
    this.#setBusy(true);

    try {
      let place = null;
      if (this.#isBrazil) {
        place = await this.#lookupAddress(postalCode, controller.signal);
        if (controller.signal.aborted) return;
        if (place?.notFound) {
          this.#lastPostalCode = '';
          this.#renderMessage(this.dataset.errorNotFound, 'error');
          return;
        }
      }

      const rates = await this.#fetchRates(postalCode, place, controller.signal);
      if (rates === null) return; // aborted
      this.#renderRates(rates, place);
      this.#lastPostalCode = postalCode;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#lastPostalCode = '';
      this.#renderMessage(error?.message || this.dataset.errorGeneric, 'error');
    } finally {
      if (this.#activeRequest === controller) {
        this.#activeRequest = null;
        this.#setBusy(false);
      }
    }
  }

  /**
   * Waits between attempts when Shopify hasn't finished pricing the carriers.
   * One retry covered every case measured on the live store; the rest are head
   * room for a slow carrier, and they only ever run when the answer is empty.
   * Worst case this adds ~4s before the customer is told nothing ships there.
   */
  static RETRY_DELAYS = [400, 600, 800, 1000, 1200];

  /**
   * @returns {Promise<Array | null>} the rates, or null if the request was aborted
   */
  async #fetchRates(postalCode, place, signal) {
    if (this.dataset.mode === 'product') return this.#fetchProductRates(postalCode, place, signal);

    let rates = await this.#requestCartRates(postalCode, place, signal);

    for (const delay of YunaShippingCalculator.RETRY_DELAYS) {
      if (rates === null || rates.length) break;
      if (!(await this.#wait(delay, signal))) return null;
      rates = await this.#requestCartRates(postalCode, place, signal);
    }

    return rates;
  }

  /**
   * Sleeps, unless the request is cancelled first.
   * @returns {Promise<boolean>} false if the wait was cut short by an abort
   */
  #wait(ms, signal) {
    return new Promise((resolve) => {
      if (signal.aborted) return resolve(false);
      const timer = setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve(true);
      }, ms);
      const onAbort = () => {
        clearTimeout(timer);
        resolve(false);
      };
      signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  /**
   * One call to the cart's rates endpoint.
   * @returns {Promise<Array | null>} the rates, or null if the request was aborted
   */
  async #requestCartRates(postalCode, place, signal) {
    const params = new URLSearchParams();
    params.set('shipping_address[zip]', postalCode);
    params.set('shipping_address[country]', this.#country);
    if (place?.province) params.set('shipping_address[province]', place.province);

    const response = await fetch(`${Theme.routes.cart_url}/shipping_rates.json?${params}`, {
      signal,
      headers: { Accept: 'application/json' },
    });

    if (signal.aborted) return null;

    if (!response.ok) {
      // A 422 body is keyed by field name, e.g. {"province":["Selecione um estado"]}.
      // Those messages come from Shopify in the shop's language, so they beat
      // anything this component could say.
      const body = await response.json().catch(() => null);
      const detail =
        body && typeof body === 'object'
          ? Object.values(body).flat().filter((v) => typeof v === 'string').join(' ')
          : '';
      throw new Error(detail || this.dataset.errorGeneric);
    }

    const body = await response.json();
    return Array.isArray(body?.shipping_rates) ? body.shipping_rates : [];
  }

  /**
   * Product page: prices the selected variant and quantity in a throwaway cart
   * made through the Storefront API (see PRODUCT_RATES_QUERY). That API gives no
   * delivery-day estimate, so product rows show the carrier, the price and the
   * carrier's own note when it has one.
   * @returns {Promise<Array | null>} rates shaped like the cart endpoint's, or null if aborted
   */
  async #fetchProductRates(postalCode, place, signal) {
    const variantId = this.#selectedVariantId();
    if (!variantId) throw new Error(this.dataset.errorGeneric);

    const deliveryAddress = { countryCode: this.#countryCode, zip: this.#format(postalCode) };
    if (place?.province) deliveryAddress.provinceCode = place.province;
    if (place?.city) deliveryAddress.city = place.city;

    const response = await fetch(STOREFRONT_ENDPOINT, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'multipart/mixed; deferSpec=20220824, application/json',
      },
      body: JSON.stringify({
        query: PRODUCT_RATES_QUERY,
        variables: {
          lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: this.#selectedQuantity() }],
          delivery: { addresses: [{ selected: true, address: { deliveryAddress } }] },
        },
      }),
    });

    if (signal.aborted) return null;
    if (!response.ok) throw new Error(this.dataset.errorGeneric);

    const payloads = await this.#readGraphQL(response);
    if (signal.aborted) return null;

    // These messages are written for developers - they quote internal ids
    // ("A mercadoria com o ID gid://shopify/ProductVariant/1 não existe.") - so
    // the customer gets the store's own generic message instead.
    const hasUserErrors = payloads.some((payload) => payload?.data?.cartCreate?.userErrors?.length);
    if (hasUserErrors) throw new Error(this.dataset.errorGeneric);

    const groups = payloads.flatMap((payload) => [
      ...(payload?.data?.cartCreate?.cart?.deliveryGroups?.nodes || []),
      ...(payload?.incremental || []).flatMap((part) => part?.data?.deliveryGroups?.nodes || []),
    ]);

    // GraphQL errors (throttling, say) come back with status 200 and no groups.
    // Passing them on as an empty list would tell the customer there is no
    // shipping to their CEP, which is a different and wrong message.
    const failed = payloads.some(
      (payload) => payload?.errors?.length || (payload?.incremental || []).some((part) => part?.errors?.length)
    );
    if (!groups.length && failed) throw new Error(this.dataset.errorGeneric);

    const seen = new Set();
    const rates = [];
    for (const group of groups) {
      for (const option of group?.deliveryOptions || []) {
        const key = option?.handle || `${option?.title}|${option?.estimatedCost?.amount}`;
        if (seen.has(key)) continue;
        seen.add(key);
        rates.push({
          name: option?.title || '',
          price: option?.estimatedCost?.amount,
          description: option?.description || '',
        });
      }
    }
    return rates;
  }

  /**
   * Reads a GraphQL response that may come as multipart/mixed (because of @defer)
   * or as one JSON body, and returns every JSON payload in order.
   * @param {Response} response
   * @returns {Promise<Array<any>>}
   */
  async #readGraphQL(response) {
    const type = response.headers.get('Content-Type') || '';
    const text = await response.text();
    const boundary = type.match(/boundary="?([^";]+)"?/i);

    if (!/multipart\/mixed/i.test(type) || !boundary) {
      try {
        return [JSON.parse(text)];
      } catch {
        return [];
      }
    }

    return text
      .split(`--${boundary[1]}`)
      .map((part) => {
        const start = part.indexOf('{');
        if (start === -1) return null;
        try {
          return JSON.parse(part.slice(start).trim());
        } catch {
          return null;
        }
      })
      .filter(Boolean);
  }

  /**
   * The variant to price: the one the page was rendered with, kept current by
   * #handleProductSelect; the add-to-cart form is the fallback.
   */
  #selectedVariantId() {
    return this.dataset.variantId || this.#productForm()?.querySelector('input[name="id"]')?.value || '';
  }

  #selectedQuantity() {
    const value = this.#productForm()?.querySelector('input[name="quantity"]')?.value;
    const quantity = parseInt(value || '', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  /** This product's add-to-cart form, looked up within the same section. */
  #productForm() {
    const scope = this.closest('.shopify-section') || document;
    return scope.querySelector('product-form-component');
  }

  /** ISO code for the Storefront API; the cart endpoint takes the country name. */
  get #countryCode() {
    if (this.#isBrazil) return 'BR';
    const country = String(this.#country).trim();
    return /^[a-z]{2}$/i.test(country) ? country.toUpperCase() : 'BR';
  }

  /**
   * Resolves a CEP to its state and city. Short timeout on purpose: a slow
   * address service should surface as a plain failure, not a hanging form.
   * @returns {Promise<{province?: string, city?: string, notFound?: boolean} | null>}
   */
  async #lookupAddress(postalCode, signal) {
    const timeout = AbortSignal.timeout(3000);
    const combined = AbortSignal.any ? AbortSignal.any([signal, timeout]) : timeout;

    try {
      const response = await fetch(`https://viacep.com.br/ws/${postalCode}/json/`, {
        signal: combined,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;

      const data = await response.json();
      // ViaCEP answers 200 with {"erro": "true"} for a well-formed but unknown CEP.
      if (data?.erro) return { notFound: true };
      return { province: data?.uf || undefined, city: data?.localidade || undefined };
    } catch {
      return null;
    }
  }

  #setBusy(busy) {
    const { button, results } = this.refs;
    this.toggleAttribute('busy', busy);
    if (button) button.disabled = busy;
    if (busy) {
      results.replaceChildren();
      this.#renderMessage(this.dataset.loading, 'loading');
    }
  }

  /**
   * @param {string} message
   * @param {'error' | 'loading' | 'empty'} kind
   */
  #renderMessage(message, kind) {
    const { status, results } = this.refs;
    results.replaceChildren();
    status.textContent = message || '';
    status.className = `yuna-shipping__status yuna-shipping__status--${kind}`;
    status.hidden = !message;
  }

  #renderRates(rates, place) {
    const { status, results } = this.refs;

    if (!rates.length) {
      this.#renderMessage(this.dataset.errorNoRates, 'empty');
      return;
    }

    // Naming the city back is how the customer verifies the CEP matched.
    if (place?.city && place?.province && this.dataset.destination) {
      status.textContent = this.dataset.destination
        .replace('{{ city }}', place.city)
        .replace('{{ province }}', place.province);
      status.className = 'yuna-shipping__status yuna-shipping__status--destination';
      status.hidden = false;
    } else {
      status.hidden = true;
      status.textContent = '';
    }

    const currency = this.dataset.currency || 'BRL';
    const locale = this.dataset.locale || 'pt-BR';
    const money = new Intl.NumberFormat(locale, { style: 'currency', currency });

    const list = document.createElement('ul');
    list.className = 'yuna-shipping__rates list-unstyled';

    for (const rate of rates) {
      const item = document.createElement('li');
      item.className = 'yuna-shipping__rate';

      const label = document.createElement('span');
      label.className = 'yuna-shipping__rate-name';

      const name = document.createElement('span');
      name.textContent = rate.presentment_name || rate.name || '';
      label.appendChild(name);

      // Either the carrier's own note (local delivery instructions) or the
      // business-day estimate — never both, to keep the row to two lines.
      const detail = rate.description || this.#deliveryEstimate(rate);
      if (detail) {
        const note = document.createElement('span');
        note.className = 'yuna-shipping__rate-note';
        note.textContent = detail;
        label.appendChild(note);
      }

      const price = document.createElement('span');
      price.className = 'yuna-shipping__rate-price';
      const amount = Number(rate.price);
      price.textContent =
        Number.isFinite(amount) && amount === 0 ? this.dataset.freeLabel || money.format(0) : money.format(amount || 0);

      item.append(label, price);
      list.appendChild(item);
    }

    results.replaceChildren(list);
  }

  /**
   * Shopify reports the estimate as a range of business days. A single-day range
   * reads better as one number than as "5–5", and one day needs the singular —
   * Sedex really does come back as [1, 1], and "1 dias úteis" is wrong.
   */
  #deliveryEstimate(rate) {
    const days = rate.delivery_days;
    if (!Array.isArray(days) || !days.length) return '';

    const low = days[0];
    const high = days[days.length - 1];

    let template;
    if (low !== high) {
      template = this.dataset.etaRange;
    } else if (low === 1) {
      template = this.dataset.etaOne || this.dataset.etaSingle;
    } else {
      template = this.dataset.etaSingle;
    }
    if (!template) return '';

    return template.replace('{{ min }}', String(low)).replace('{{ max }}', String(high));
  }
}

if (!customElements.get('yuna-shipping-calculator')) {
  customElements.define('yuna-shipping-calculator', YunaShippingCalculator);
}
